/* eslint-disable tailwindcss/migration-from-tailwind-2 */
import {
  Badge,
  DataTable,
  useSWRDataTable,
  useTableContext,
} from "@bleu.builders/ui";
import { ArrowRight } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import { withPaywall } from "#/components/WithPaywall";
import { formatDate } from "#/lib/formatDate";
import { UserPagePath } from "#/lib/pagesHelper";
import { canViewNewsArticle } from "#/lib/permissions";
import { cn, truncateHtmlContent } from "#/lib/utils";
import { useSWRDataTableWithRedirect } from "#/utils/useSWRDataTableWithRedirect";

export const CATEGORY_COLORS = {
  "Notícias Tributárias": "text-[#0090FF] border-[#0090FF] bg-[#0090FF]",
  Artigos: "text-[#E53D00] border-[#E53D00] bg-[#E53D00]",
  "Atualizações de Teses": "text-[#046865] border-[#046865] bg-[#046865]",
  "Dica da Click": "text-[#C69500] border-[#C69500] bg-[#C69500]",
};

const NewsList = ({ newsArticles, type, showSeeMore = false }) => {
  return (
    <div className="grid-cols-1">
      <div className="flex relative flex-row pb-2 mb-4">
        <div className="absolute left-0 bottom-0 border-b-2 border-black w-1/2" />
        <h2 className="text-2xl font-semibold font-title">{type}</h2>
        <div className="content-center ml-3">
          {showSeeMore && (
            <Link to="latest" className="">
              <Badge className="text-sm px-2 border-0">
                Ver mais
                <ArrowRight className="ml-2" size={16} />
              </Badge>
            </Link>
          )}
        </div>
      </div>
      {newsArticles.map((newsArticle) => (
        <Link to={String(newsArticle.id)}>
          <div className="mb-6 hover:scale-[101%]">
            <h3
              className={cn(
                "font-semibold",
                CATEGORY_COLORS[newsArticle.category],
                "bg-background",
              )}
            >
              {newsArticle.category}
            </h3>
            <p className="font-medium">{newsArticle.title}</p>
            <span className="text-sm text-gray-600">
              {formatDate(newsArticle.published_at)}
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
};

const HeaderNews = ({ newsArticle }) => {
  if (!newsArticle) return null;

  return (
    <Link to={String(newsArticle.id)}>
      <div className="mx-auto hover:scale-[101%]">
        <div>
          <div className="mb-4">
            <span className="text-sm text-blue-500 font-semibold uppercase">
              {newsArticle.category}
            </span>
            <span className="text-sm text-gray-600 ml-4">
              {formatDate(newsArticle.published_at)}
            </span>
          </div>
          <div className="mb-10">
            <h1 className="text-3xl font-bold mb-2 font-title">
              {newsArticle.title}
            </h1>
            <div className="">{truncateHtmlContent(newsArticle.content)}</div>
          </div>
        </div>
      </div>
    </Link>
  );
};

function TableItems() {
  // @ts-expect-error TS(2339) FIXME: Property 'table' does not exist on type '{}'.
  const { table } = useTableContext();

  const getRowItem = (row, name) => {
    return row
      .getVisibleCells()
      .find((cell) => cell.column.id === name)
      .getValue();
  };
  return (
    <>
      <div className="flex flex-col gap-4">
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => {
            return (
              <Link key={row.id} to={String(row.original.id)}>
                <div className="space-y-1 hover:scale-[101%]">
                  <p className="font-medium text-sm">
                    {getRowItem(row, "title")}
                  </p>
                  <span className="text-xs">
                    {formatDate(getRowItem(row, "published_at"))}
                  </span>
                </div>
              </Link>
            );
          })
        ) : (
          <div className="mt-4 w-full mr-4 overflow-y-auto max-h-[80vh] min-h-[10vh]">
            No results, upload a file!
          </div>
        )}
      </div>
    </>
  );
}

export function NewsArticlesTable({
  data,
  error,
  tableState,
  setTableState,
  category,
}) {
  return (
    <div>
      <DataTable
        data={data}
        error={error}
        tableState={tableState}
        setTableState={setTableState}
        setSelectedData={null}
        setQueryToParams={false}
      >
        <div className="max-w-xl mx-auto rounded-lg">
          <h2
            className={cn(
              "text-xl font-semibold border-b-2 mb-4 pb-2",
              CATEGORY_COLORS[category],
              "bg-background",
              "font-title",
            )}
          >
            {category}
          </h2>

          <TableItems />
        </div>
      </DataTable>
    </div>
  );
}

function RecentNewsArticles() {
  const { data, error, isLoading, tableState, setTableState } =
    useSWRDataTableWithRedirect(UserPagePath.NEWS_ARTICLES);

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center max-h-[80vh] min-h-[80vh] animate-pulse text-3xl">
        Espere um segundo. Estamos carregando os dados para você...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center max-h-[80vh] min-h-[80vh] text-3xl">
        Ocorreu um erro ao carregar os dados. Por favor, tente novamente mais
        tarde.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <HeaderNews newsArticle={data?.latest[0]} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NewsList
            newsArticles={data?.latest.slice(1)}
            type="Últimos conteúdos"
            showSeeMore
          />
          {data?.recommended && (
            <NewsList newsArticles={data?.recommended} type="Recomendados" />
          )}
        </div>
      </div>
      <div className="grid grid-cols-4 gap-4 items-stretch">
        {data?.data.map((group) => {
          return (
            <div key={group.category} className="col-span-1 flex flex-col">
              <NewsArticlesTable
                data={{
                  data: group.data,
                  columns: data.columns,
                  filters: data.filters,
                  search: data.search,
                }}
                error={error}
                tableState={tableState}
                setTableState={setTableState}
                category={group.category}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default withPaywall(RecentNewsArticles, canViewNewsArticle);
